import { VFC, useState } from 'react';
import {
  Col,
  Container,
  Hidden,
  Row,
  Visible,
  useScreenClass,
} from 'react-grid-system';
import styled from 'styled-components';
import { useSettings } from '../../contexts/settingsContext';
import { tOrderLine } from '../../contexts/zod';
import useStatusMessage from '../../hooks/useStatusMessage';
import { theme } from '../../utils/theme';
import Divider from '../atoms/Divider';
import Spacing from '../atoms/Spacing';
import SvgIcon from '../atoms/SvgIcon';
import Typography from '../atoms/Typography';
import IconButton from '../molecules/IconButton';
import ProductImage from '../molecules/ProductImage';
import StatusBox from '../molecules/StatusBox';
import TableItem from '../molecules/TableItem';
import OrderLineForm from './form/OrderLineForm';

interface Props {
  radius: string;
  addedToReturns: boolean;
}

const StyledOrderLine = styled.div<Props>`
  background-color: white;
  margin-bottom: 3.2rem;
  border-radius: ${({ radius }) => radius};
  padding: 3.2rem;
  border: ${({ addedToReturns }) =>
    addedToReturns ? '2px solid #4CAF50' : '1px solid #e0e0e0'};
`;

const OrderLine: VFC<tOrderLine> = (props) => {
  const [open, setOpen] = useState(false);
  const { cornerRadius: radius } = useSettings();
  const screenClass = useScreenClass();

  const {
    productNumber,
    variant,
    quantity,
    variantDescription,
    imageUrl,
    labelTranslationKey,
    mayBeReturned,
    mayBeClaimed,
    description,
  } = props;

  const { exists, numberOfItems } = useStatusMessage(props);

  const table = [
    {
      title: <Typography translateKey="art_no" variant="body1-semi-bold" />,
      value: `${productNumber}${variant}`,
    },
    {
      title: (
        <Typography translateKey="product_name" variant="body1-semi-bold" />
      ),
      value: description,
    },
    {
      title: (
        <Typography translateKey="product_variant" variant="body1-semi-bold" />
      ),
      value: variantDescription,
    },
    {
      title: <Typography translateKey="quantity" variant="body1-semi-bold" />,
      value: quantity,
    },
  ];

  return (
    <StyledOrderLine radius={radius} addedToReturns={exists}>
      <Container>
        <Row align="start">
          {imageUrl && (
            <Col xs={12} sm={12} md="content">
              <Row justify="start">
                <ProductImage src={imageUrl} />
              </Row>
              <Hidden md lg xl xxl xxxl>
                <Spacing spacing={3} />
              </Hidden>
            </Col>
          )}
          <Col>
            <Hidden md lg xl xxl xxxl>
              <Spacing />
            </Hidden>
            <Row>
              <Col>
                <Row nogutter={['md', 'lg', 'xl', 'xxl'].includes(screenClass)}>
                  <StatusBox title={labelTranslationKey} />
                </Row>
              </Col>
            </Row>
            <Spacing />
            {exists && (
              <Row>
                <Col xs="content">
                  <Spacing />
                  <Row
                    nogutter={['md', 'lg', 'xl', 'xxl'].includes(screenClass)}
                    align="center"
                  >
                    <Hidden xs sm>
                      <SvgIcon icon="package" iconSize="large" color="black" />
                      <Spacing horizontal />
                    </Hidden>
                    <Typography
                      translateKey="products_in_return_basket"
                      translateParams={{ added: numberOfItems, quantity }}
                      variant="h5"
                    />
                  </Row>
                  <Spacing />
                </Col>
              </Row>
            )}

            <Row>
              {table.map(({ title, value }) => (
                <Col key={value} xs={12} sm={12} md={6} lg="content">
                  <Visible xs sm md>
                    <Spacing />
                  </Visible>
                  <Row>
                    <Col xs="content">
                      <Row
                        nogutter={['md', 'lg', 'xl', 'xxl'].includes(
                          screenClass,
                        )}
                      >
                        <TableItem key={value} title={title} value={value} />
                      </Row>
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
          </Col>
          {(mayBeReturned || mayBeClaimed) && quantity - numberOfItems !== 0 && (
            <IconButton
              icon={open ? 'minus' : 'plus'}
              iconColor={theme.colors.grey[100]}
              backgroundColor={theme.colors.grey[40]}
              onClick={() => setOpen(!open)}
            />
          )}
        </Row>

        {open && quantity - numberOfItems !== 0 && (
          <>
            <Spacing
              spacing={['lg', 'xl', 'xxl'].includes(screenClass) ? 3 : 2}
            />
            <Row align="center">
              <Typography
                translateKey="fill_in_form"
                variant={
                  ['lg', 'xl', 'xxl'].includes(screenClass) ? 'h3' : 'h5'
                }
              />
              <Spacing horizontal />
              <Divider />
            </Row>
            <Spacing spacing={3} />
          </>
        )}
      </Container>
      {open && quantity - numberOfItems !== 0 && (
        <OrderLineForm {...props} layout="orderLine" setOpen={setOpen} />
      )}
    </StyledOrderLine>
  );
};

export default OrderLine;
